/**
 * Gallery — file gallery with masonry grid, drag-drop upload, Wite AI integration.
 * Replaces window.Gallery.
 */
import { useState, useEffect, useLayoutEffect, useRef, useCallback, type FC } from 'react';
import { Icon } from './Icon';
import { GalleryHeader } from './GalleryHeader';
import { FolderManager } from './FolderManager';
import * as dataService from '../services/dataService';
import { apiPost, apiPostForm, getToken } from '../services/api';
import type { FileItem } from '../stores/appStore';

interface WiteFile {
  id?: string | number;
  imageUrl?: string;
  thumbnail_url?: string;
  thumbnailUrl?: string;
  prompt?: string;
  model?: string;
  original_filename?: string;
  filename?: string;
  name?: string;
}

interface Props {
  files: FileItem[];
  auth: { password: string };
  init: () => Promise<void>;
  onAddToCollection?: (f: FileItem) => void;
  onDeleteFile?: (name: string) => void;
  activeSubTab: string;
  onSubTabChange: (tab: string) => void;
  galleryType?: string;
}

export const Gallery: FC<Props> = ({
  files,
  auth,
  init,
  onAddToCollection,
  onDeleteFile,
  activeSubTab,
  onSubTabChange,
  galleryType = 'upload',
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [filter, setFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('week');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [filedFiles, setFiledFiles] = useState<Set<string>>(new Set());
  const [creatingArticleFiles, setCreatingArticleFiles] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ message: string; kind: string } | null>(null);
  const [areFoldersLoading, setAreFoldersLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const masonryRef = useRef<HTMLDivElement>(null);
  const masonryRafRef = useRef<number | null>(null);

  // Wite AI state
  const [isWiteAiMode, setIsWiteAiMode] = useState(false);
  const [witeApiUrl, setWiteApiUrl] = useState(() => localStorage.getItem('wite_api_url') || '');
  const [witeApiKey, setWiteApiKey] = useState(() => localStorage.getItem('wite_api_key') || '');
  const [witeFiles, setWiteFiles] = useState<WiteFile[]>([]);
  const [witeLoading, setWiteLoading] = useState(false);
  const [witeError, setWiteError] = useState<string | null>(null);
  const [isConfiguringWite, setIsConfiguringWite] = useState(false);

  const showToast = useCallback((message: string, kind = 'success') => {
    setToast({ message, kind });
    setTimeout(() => setToast(null), 2600);
  }, []);

  const scheduleMasonryLayout = useCallback(() => {
    if (masonryRafRef.current) cancelAnimationFrame(masonryRafRef.current);
    masonryRafRef.current = requestAnimationFrame(() => {
      masonryRafRef.current = null;
      const grid = masonryRef.current;
      if (!grid) return;
      const styles = getComputedStyle(grid);
      const rowHeight = parseFloat(styles.getPropertyValue('grid-auto-rows')) || 8;
      const rowGap = parseFloat(styles.getPropertyValue('gap')) || 0;
      grid.querySelectorAll('.masonry-item').forEach((item) => {
        const media = item.querySelector('img');
        const h = (media || item).getBoundingClientRect().height;
        if (!h) return;
        (item as HTMLElement).style.gridRowEnd =
          `span ${Math.ceil((h + rowGap) / (rowHeight + rowGap))}`;
      });
    });
  }, []);

  useEffect(() => {
    window.addEventListener('resize', scheduleMasonryLayout);
    return () => window.removeEventListener('resize', scheduleMasonryLayout);
  }, [scheduleMasonryLayout]);

  useLayoutEffect(() => {
    setFiledFiles(new Set());
    setAreFoldersLoading(activeSubTab !== 'cloud');
    if (activeSubTab !== 'files') setIsWiteAiMode(false);
  }, [activeSubTab, galleryType]);

  useEffect(() => {
    if (isWiteAiMode && witeApiUrl && witeApiKey && !isConfiguringWite) fetchRemoteGallery();
  }, [isWiteAiMode]);

  const fetchRemoteGallery = async (urlOverride?: string, keyOverride?: string) => {
    const url = urlOverride || witeApiUrl;
    const key = keyOverride || witeApiKey;
    if (!url || !key) return;
    setWiteLoading(true);
    setWiteError(null);
    try {
      const base = url.replace(/\/$/, '');
      const res = await fetch(`${base}/api/external_gallery?key=${key}`);
      if (!res.ok) throw new Error(`Status: ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setWiteFiles(data);
        setIsConfiguringWite(false);
        localStorage.setItem('wite_api_url', url);
        localStorage.setItem('wite_api_key', key);
      } else throw new Error(data.error || 'Invalid response');
    } catch (e: any) {
      setWiteError(e.message);
    } finally {
      setWiteLoading(false);
    }
  };

  const handleSaveWiteSettings = () => {
    const cleanUrl = witeApiUrl.trim();
    const cleanKey = witeApiKey.trim();
    setWiteApiUrl(cleanUrl);
    setWiteApiKey(cleanKey);
    localStorage.setItem('wite_api_url', cleanUrl);
    localStorage.setItem('wite_api_key', cleanKey);
    setIsConfiguringWite(false);
    fetchRemoteGallery(cleanUrl, cleanKey);
  };

  const handleResetSettings = () => {
    if (!confirm('Сбросить сохраненные настройки подключения?')) return;
    localStorage.removeItem('wite_api_url');
    localStorage.removeItem('wite_api_key');
    setWiteApiUrl('');
    setWiteApiKey('');
    setWiteFiles([]);
    setWiteError(null);
    setIsConfiguringWite(true);
  };

  const resolveWiteAbsoluteUrl = (u?: string) => {
    if (!u) return '';
    if (u.startsWith('http')) return u;
    const base = witeApiUrl.replace(/\/$/, '');
    return `${base}${u.startsWith('/') ? '' : '/'}${u}`;
  };

  const resolveWiteThumbUrl = (file: WiteFile) => {
    let p = file.thumbnail_url || file.thumbnailUrl;
    if (!p && file.imageUrl?.includes('/images/'))
      p = file.imageUrl.replace('/images/', '/thumbnails/');
    if (!p) p = file.imageUrl;
    return resolveWiteAbsoluteUrl(p);
  };

  const handleLinkRemote = async (file: WiteFile) => {
    if (!file.imageUrl) return alert('URL изображения отсутствует');
    setIsUploading(true);
    try {
      const base = witeApiUrl.replace(/\/$/, '');
      let fileUrl = file.imageUrl;
      if (!fileUrl.startsWith('http'))
        fileUrl = `${base}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
      await apiPost('/api/files/link-remote', {
        url: fileUrl,
        name: file.original_filename || file.filename || file.name || `remote_${Date.now()}.png`,
        prompt: file.prompt,
        model: file.model,
        thumb: resolveWiteThumbUrl(file) || fileUrl,
      });
      await init();
    } catch (e: any) {
      alert('Ошибка привязки: ' + e.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImportFromRemote = async (file: WiteFile) => {
    if (!file.imageUrl) return alert('URL отсутствует');
    setIsUploading(true);
    try {
      let fileUrl = file.imageUrl;
      if (!fileUrl.startsWith('http')) {
        const base = witeApiUrl.replace(/\/$/, '');
        fileUrl = `${base}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
      }
      const imgRes = await fetch(fileUrl);
      const blob = await imgRes.blob();
      const fd = new FormData();
      fd.append('type', galleryType);
      fd.append('files', blob, `wite_import_${Date.now()}.${blob.type.split('/')[1] || 'png'}`);
      await apiPostForm('/api/files/upload', fd);
      await init();
    } catch (e: any) {
      alert('Ошибка импорта: ' + e.message);
    } finally {
      setIsUploading(false);
    }
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 72;

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, dateFilter, activeSubTab, isWiteAiMode]);

  const handleFolderChange = (folders: Record<string, string[]>) => {
    const filed = new Set<string>();
    Object.values(folders).forEach((list) => list.forEach((f) => filed.add(f)));
    setFiledFiles(filed);
  };

  const toggleSelect = (fileName: string) => {
    setSelectedFiles((prev) => {
      const n = new Set(prev);
      if (n.has(fileName)) n.delete(fileName);
      else n.add(fileName);
      return n;
    });
  };

  const handleUploadFiles = async (fileList: FileList | null) => {
    if (!fileList || !fileList.length) return;
    setIsUploading(true);
    const totalFiles = fileList.length;
    const BATCH_SIZE = 5;
    setUploadProgress({ current: 0, total: totalFiles });
    try {
      for (let start = 0; start < totalFiles; start += BATCH_SIZE) {
        const end = Math.min(start + BATCH_SIZE, totalFiles);
        const fd = new FormData();
        fd.append('type', galleryType);
        for (let i = start; i < end; i++) fd.append('files', fileList[i]);
        await apiPostForm('/api/files/upload', fd);
        setUploadProgress({ current: end, total: totalFiles });
      }
      await init();
    } catch {
      alert('Ошибка загрузки');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedFiles.size || !confirm(`Удалить выбранные файлы (${selectedFiles.size} шт.)?`))
      return;
    try {
      for (const name of selectedFiles) await dataService.deleteFile({ filename: name });
      setSelectedFiles(new Set());
      await init();
    } catch {
      alert('Ошибка при удалении');
    }
  };

  const handleRename = async (file: FileItem) => {
    const cur = file.name.replace(/\.[^/.]+$/, '');
    const newName = prompt('Новое имя файла:', cur);
    if (newName && newName !== cur) {
      try {
        await apiPost('/api/files/rename', {
          filename: file.name,
          new_name: newName,
          type: galleryType,
        });
        await init();
      } catch {
        alert('Ошибка при переименовании');
      }
    }
  };

  const handleCreateCloudArticleFromSource = async (file: FileItem) => {
    if (!file?.name) return showToast('Не удалось определить источник', 'error');
    const busyKey = file.name;
    setCreatingArticleFiles((prev) => new Set(prev).add(busyKey));
    try {
      const baseName = file.name.split('/').pop() || file.name;
      const printName = baseName.replace(/\.[^/.]+$/, '');
      const isRemoteLinked = file.type === 'linked' || file.is_remote;
      const srcUrl = file.url.startsWith('http')
        ? file.url
        : `${window.location.origin}${file.url.startsWith('/') ? '' : '/'}${file.url}`;
      const result = await dataService.createCloudArticleFromSource({
        sourceName: isRemoteLinked ? `linked/${baseName}` : file.name,
        printName,
        sourceRemote: !!isRemoteLinked,
        sourceUrl: isRemoteLinked ? srcUrl : '',
        sourceThumb: isRemoteLinked ? srcUrl : '',
      });
      if (!result?.article) throw new Error('Не удалось создать артикул');
      await init();
      showToast(
        result.existing
          ? `Артикул уже существует: ${result.article}`
          : `Артикул создан: ${result.article}`,
      );
    } catch (e: any) {
      showToast('Ошибка: ' + e.message, 'error');
    } finally {
      setCreatingArticleFiles((prev) => {
        const n = new Set(prev);
        n.delete(busyKey);
        return n;
      });
    }
  };

  const currentList: any[] = isWiteAiMode ? witeFiles : files;

  // --- handleCreateCloudArticleFromWite (for Wite AI files) ---
  const getWiteSourceName = (file: WiteFile) => {
    return file.original_filename || file.filename || file.name || `wite_${file.id || Date.now()}.png`;
  };

  const handleCreateCloudArticleFromWite = async (file: WiteFile) => {
    if (!file) return;
    const busyKey = getWiteSourceName(file);
    setCreatingArticleFiles((prev) => new Set(prev).add(busyKey));
    try {
      const printName = (file.prompt || file.model || `wite_${file.id || Date.now()}`).trim();
      const result = await dataService.createCloudArticleFromSource({
        sourceName: getWiteSourceName(file),
        printName,
        sourceRemote: true,
        sourceUrl: resolveWiteAbsoluteUrl(file.imageUrl),
        sourceThumb: resolveWiteThumbUrl(file) || resolveWiteAbsoluteUrl(file.imageUrl),
      });
      if (!result?.article) throw new Error('Не удалось создать артикул');
      await init();
      showToast(result.existing ? `Артикул уже существует: ${result.article}` : `Артикул создан: ${result.article}`);
    } catch (e: any) {
      showToast('Ошибка создания артикула: ' + e.message, 'error');
    } finally {
      setCreatingArticleFiles((prev) => { const n = new Set(prev); n.delete(busyKey); return n; });
    }
  };

  // --- handleDownloadFile (proxy download for remote/linked, direct for local) ---
  const handleDownloadFile = async (file: FileItem) => {
    const fileUrl = getFileUrl(file);
    if (!fileUrl) return;
    const sourceName = String(file.name || '').trim();
    const sourceUrl = /^https?:\/\//i.test(fileUrl)
      ? fileUrl
      : `${window.location.origin}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
    const remoteBase = (() => {
      try { return new URL(sourceUrl, window.location.origin).pathname.split('/').pop() || ''; }
      catch { return ''; }
    })();
    const localBase = sourceName.split('/').pop() || sourceName;
    const finalName = (remoteBase || localBase || 'file').trim();
    try {
      const isRemote = /^https?:\/\//i.test(fileUrl);
      if (isRemote || file.type === 'linked') {
        const params = new URLSearchParams();
        if (sourceName) params.set('source_name', sourceName);
        params.set('source_url', sourceUrl);
        params.set('filename', finalName);
        const headers: Record<string, string> = {};
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`/api/files/download-source?${params.toString()}`, { cache: 'no-store', headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (!blob.size) throw new Error('Пустой файл');
        const { saveAs } = await import('file-saver');
        saveAs(blob, finalName);
      } else {
        const res = await fetch(fileUrl, { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const { saveAs } = await import('file-saver');
        saveAs(blob, finalName);
      }
    } catch (e: any) {
      showToast(`Ошибка скачивания: ${e.message || 'неизвестная ошибка'}`, 'error');
    }
  };

  // --- handleCopyFileUrl with prompt() fallback ---
  const handleCopyFileUrl = async (file: FileItem) => {
    // Prefer short_url for products, then url
    const url = (file.category === 'products' && (file as any).short_url)
      ? (file as any).short_url
      : getFileUrl(file);
    if (!url) return alert('Ссылка недоступна');
    const absoluteUrl = url.startsWith('http')
      ? url
      : `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(absoluteUrl);
        showToast('Ссылка скопирована');
      } else {
        prompt('Скопируйте ссылку:', absoluteUrl);
      }
    } catch {
      prompt('Скопируйте ссылку:', absoluteUrl);
    }
  };

  const filteredFiles = currentList.filter((f: any) => {
    if (isWiteAiMode) {
      if (!filter) return true;
      return (
        (f.prompt || '').toLowerCase().includes(filter.toLowerCase()) ||
        (f.model || '').toLowerCase().includes(filter.toLowerCase())
      );
    }
    const ft = f.type || 'upload';
    if (ft !== galleryType && !(galleryType === 'upload' && ft === 'linked')) return false;
    if (!f.name.toLowerCase().includes(filter.toLowerCase())) return false;
    if (dateFilter !== 'all') {
      const now = Date.now();
      const fileTime = f.mtime * 1000;
      const day = 86400000;
      if (dateFilter === 'today') {
        const ts = new Date();
        ts.setHours(0, 0, 0, 0);
        if (fileTime < ts.getTime()) return false;
      } else if (dateFilter === 'week' && now - fileTime > 7 * day) return false;
      else if (dateFilter === 'month' && now - fileTime > 30 * day) return false;
    }
    if (filedFiles.has(f.name) && !filter) return false;
    return true;
  });

  const sortedFiles = [...filteredFiles].sort((a: any, b: any) => {
    if (isWiteAiMode) return 0;
    if (a.type === 'cloud' && b.type === 'cloud') {
      const ac = Number(a.article_created_mtime || 0);
      const bc = Number(b.article_created_mtime || 0);
      if (ac !== bc) return bc - ac;
    }
    return Number(b.mtime || 0) - Number(a.mtime || 0);
  });

  const totalPages = Math.ceil(sortedFiles.length / ITEMS_PER_PAGE);
  const displayedFiles = sortedFiles.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useLayoutEffect(() => {
    scheduleMasonryLayout();
    return () => {
      if (masonryRafRef.current) cancelAnimationFrame(masonryRafRef.current);
    };
  }, [displayedFiles, isWiteAiMode, activeSubTab, scheduleMasonryLayout]);

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  const getFileUrl = (f: any) => f.url || f.thumb || f.imageUrl || '';
  const getLinkedThumbForDisplay = (f: any) => {
    const t = f.thumb || f.url || '';
    if (!t) return '';
    if ((f.type === 'linked' || f.is_remote) && t.includes('/images/'))
      return t.replace('/images/', '/thumbnails/');
    return t;
  };

  return (
    <div
      className="h-full flex flex-col"
      onDragOver={(e) => {
        e.preventDefault();
        e.currentTarget.classList.add('bg-blue-500/10');
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.currentTarget.classList.remove('bg-blue-500/10');
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.currentTarget.classList.remove('bg-blue-500/10');
        if (e.dataTransfer.files?.length) handleUploadFiles(e.dataTransfer.files);
      }}
    >
      <GalleryHeader
        activeSubTab={activeSubTab}
        onSubTabChange={onSubTabChange}
        filter={filter}
        setFilter={setFilter}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        onWiteAiToggle={() => setIsWiteAiMode(!isWiteAiMode)}
        isWiteAiMode={isWiteAiMode}
      />

      <div id="gallery-container" className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
        {isWiteAiMode ? (
          isConfiguringWite || !witeApiUrl || !witeApiKey ? (
            <div className="flex flex-col items-center justify-center p-8 bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700 max-w-lg mx-auto mt-10 shadow-2xl">
              <div className="bg-indigo-500/20 p-4 rounded-full mb-6">
                <Icon name="settings" className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Настройка подключения</h3>
              <p className="text-slate-400 text-center mb-6 text-sm">
                Введите URL и API ключ вашего Wite AI инстанса
              </p>
              <input
                type="text"
                placeholder="URL (например, https://wite-ai.site)"
                value={witeApiUrl}
                onChange={(e) => setWiteApiUrl(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white mb-3 focus:border-indigo-500 outline-none"
              />
              <input
                type="password"
                placeholder="API Key"
                value={witeApiKey}
                onChange={(e) => setWiteApiKey(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white mb-6 focus:border-indigo-500 outline-none"
              />
              <div className="flex gap-2 w-full">
                <button
                  onClick={handleResetSettings}
                  className="px-4 bg-red-500/10 hover:bg-red-500/20 text-red-500 font-bold py-3 rounded-xl transition-colors border border-red-500/20"
                  title="Сбросить"
                >
                  <Icon name="trash-2" className="w-5 h-5" />
                </button>
                {witeApiKey && (
                  <button
                    onClick={() => setIsConfiguringWite(false)}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-xl transition-colors"
                  >
                    Отмена
                  </button>
                )}
                <button
                  onClick={handleSaveWiteSettings}
                  disabled={!witeApiUrl || !witeApiKey}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-50"
                >
                  Подключиться
                </button>
              </div>
            </div>
          ) : witeLoading ? (
            <div className="flex justify-center items-center h-64">
              <Icon name="loader-2" className="w-10 h-10 text-indigo-500 animate-spin" />
            </div>
          ) : witeError ? (
            <div className="text-center p-8 bg-slate-800/50 rounded-2xl border border-red-500/30 max-w-lg mx-auto mt-10">
              <p className="text-red-400 font-medium mb-2">Ошибка подключения</p>
              <p className="text-slate-400 text-sm mb-4">{witeError}</p>
              <button
                onClick={handleSaveWiteSettings}
                className="text-indigo-400 hover:text-indigo-300 underline text-sm"
              >
                Повторить
              </button>
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <button
                  onClick={() => {
                    setIsConfiguringWite(true);
                    setWiteError(null);
                  }}
                  className="text-slate-500 hover:text-white text-xs"
                >
                  Настроить подключение
                </button>
              </div>
            </div>
          ) : (
            <div className="masonry-grid pb-20" ref={masonryRef}>
              {displayedFiles.map((file: WiteFile, idx: number) => {
                const thumbUrl = resolveWiteThumbUrl(file);
                if (!thumbUrl) return null;
                return (
                  <div
                    key={idx}
                    className="masonry-item mb-4 group relative rounded-xl overflow-hidden bg-slate-800 border border-slate-700/50 hover:border-indigo-500 transition-all shadow-lg"
                  >
                    <img
                      src={thumbUrl}
                      alt={file.prompt}
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-110"
                      style={{ minHeight: '100px' }}
                      loading="lazy"
                      onLoad={scheduleMasonryLayout}
                    />
                    <div className="absolute inset-0 bg-slate-900/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-4 gap-3 backdrop-blur-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleLinkRemote(file)}
                          disabled={isUploading}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-2 shadow-lg"
                          title="Добавить ссылку"
                        >
                          <Icon name="link" className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleImportFromRemote(file)}
                          disabled={isUploading}
                          className="bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-2 shadow-lg"
                          title="Скачать"
                        >
                          {isUploading ? (
                            <Icon name="loader-2" className="w-3 h-3 animate-spin" />
                          ) : (
                            <Icon name="image-down" className="w-3 h-3" />
                          )}
                        </button>
                        <button
                          onClick={() => handleCreateCloudArticleFromWite(file)}
                          disabled={creatingArticleFiles.has(getWiteSourceName(file))}
                          className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-2 shadow-lg disabled:opacity-60"
                          title="Создать артикул"
                        >
                          {creatingArticleFiles.has(getWiteSourceName(file)) ? (
                            <Icon name="loader-2" className="w-3 h-3 animate-spin" />
                          ) : (
                            <Icon name="cloud" className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="absolute top-2 right-2 bg-black/60 px-1.5 py-0.5 rounded text-[9px] text-indigo-300 font-mono border border-white/10 backdrop-blur-md">
                      {file.model}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <>
            {(activeSubTab === 'files' || activeSubTab === 'publication') && (
              <div className="mb-6">
                <FolderManager
                  key={`${galleryType}_${activeSubTab}`}
                  files={files}
                  title="Организация файлов"
                  galleryType={galleryType}
                  auth={auth}
                  onAddToCollection={onAddToCollection}
                  onDeleteFile={onDeleteFile}
                  toggleSelect={toggleSelect}
                  selectedFiles={selectedFiles}
                  onRenameFile={handleRename}
                  onFolderChange={handleFolderChange}
                  onLoading={setAreFoldersLoading}
                />
              </div>
            )}

            {!areFoldersLoading && (activeSubTab === 'files' || activeSubTab === 'publication') && (
              <div
                className={`mb-6 p-1 rounded-2xl border-2 border-dashed transition-all ${isUploading ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-800 hover:border-slate-600'}`}
              >
                <label className="flex items-center justify-center gap-4 py-8 cursor-pointer group">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isUploading ? 'bg-indigo-500 text-white' : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700 group-hover:text-white'}`}
                  >
                    {isUploading ? (
                      <Icon name="loader-2" className="w-6 h-6 animate-spin" />
                    ) : (
                      <Icon name="upload" className="w-6 h-6" />
                    )}
                  </div>
                  <div className="text-left">
                    <p
                      className={`font-bold transition-colors ${isUploading ? 'text-indigo-400' : 'text-slate-300 group-hover:text-white'}`}
                    >
                      {isUploading && uploadProgress
                        ? `Загрузка ${uploadProgress.current} / ${uploadProgress.total}`
                        : 'Загрузить файлы'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {isUploading && uploadProgress
                        ? `${Math.round((uploadProgress.current / uploadProgress.total) * 100)}%`
                        : 'PNG, JPG до 10MB'}
                    </p>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUploadFiles(e.target.files)}
                    disabled={isUploading}
                  />
                </label>
              </div>
            )}

            {!areFoldersLoading && (
              <div className="masonry-grid pb-20" ref={masonryRef}>
                {displayedFiles.map((file: FileItem) => {
                  const isSelected = selectedFiles.has(file.name);
                  const fileUrl = getFileUrl(file);
                  const canPreview = !!fileUrl;
                  const canCreateCloud =
                    activeSubTab === 'files' && (file.type === 'upload' || file.type === 'linked');
                  const isCreating = creatingArticleFiles.has(file.name);

                  return (
                    <div
                      key={file.name}
                      className={`masonry-item mb-4 group relative rounded-xl overflow-hidden bg-slate-900 border transition-all cursor-pointer shadow-lg hover:shadow-xl ${isSelected ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-slate-800 hover:border-slate-600'}`}
                      onClick={() => toggleSelect(file.name)}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('fileName', file.name);
                      }}
                    >
                      <div className="absolute inset-0 bg-slate-800 animate-pulse pointer-events-none" />
                      <img
                        src={getLinkedThumbForDisplay(file)}
                        alt={file.name}
                        className="w-full h-auto relative z-10 transition-transform duration-500 group-hover:scale-105"
                        style={{ minHeight: 100 }}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          if (
                            (file.type === 'linked' || file.is_remote) &&
                            file.url &&
                            (e.target as HTMLImageElement).src !== file.url
                          )
                            (e.target as HTMLImageElement).src = file.url;
                        }}
                        onLoad={(e) => {
                          const prev = (e.target as HTMLElement)
                            .previousElementSibling as HTMLElement;
                          if (prev) prev.style.display = 'none';
                          scheduleMasonryLayout();
                        }}
                      />

                      <div
                        className="absolute inset-0 bg-slate-900/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-20 backdrop-blur-sm overflow-hidden"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canPreview) {
                            setPreviewFile({ ...file, url: fileUrl });
                            setPreviewZoom(1);
                          }
                        }}
                        style={{ cursor: canPreview ? 'zoom-in' : 'default' }}
                      >
                        <div className="flex flex-wrap items-center justify-center gap-1.5 p-2 max-h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleCopyFileUrl(file)}
                            className="flex items-center gap-1.5 px-2.5 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white text-xs font-medium transition-all backdrop-blur-sm"
                            title="Ссылка"
                          >
                            <Icon name="link" className="w-4 h-4 shrink-0" />
                            <span className="hidden gallery-btn-label">Ссылка</span>
                          </button>
                          <button
                            onClick={() => handleDownloadFile(file)}
                            className="flex items-center gap-1.5 px-2.5 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white text-xs font-medium transition-all backdrop-blur-sm"
                            title="Скачать"
                          >
                            <Icon name="image-down" className="w-4 h-4 shrink-0" />
                            <span className="hidden gallery-btn-label">Скачать</span>
                          </button>
                          {onAddToCollection && (
                            <button
                              onClick={() => onAddToCollection(file)}
                              className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-indigo-500/40 hover:bg-indigo-500/60 border border-indigo-400/30 rounded-xl text-white text-xs font-semibold transition-all backdrop-blur-sm shadow-lg shadow-indigo-500/10"
                              title="В коллекцию"
                            >
                              <Icon name="plus" className="w-4 h-4 shrink-0" />
                              <span className="hidden gallery-btn-label">В коллекцию</span>
                            </button>
                          )}
                          <button
                            onClick={() => handleRename(file)}
                            className="flex items-center gap-1.5 px-2.5 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white text-xs font-medium transition-all backdrop-blur-sm"
                            title="Переименовать"
                          >
                            <Icon name="pencil" className="w-4 h-4 shrink-0" />
                            <span className="hidden gallery-btn-label">Переименовать</span>
                          </button>
                          {canCreateCloud && (
                            <button
                              onClick={() => handleCreateCloudArticleFromSource(file)}
                              disabled={isCreating}
                              className="flex items-center gap-1.5 px-2.5 py-2 bg-blue-500/30 hover:bg-blue-500/50 border border-blue-400/20 rounded-lg text-white text-xs font-medium disabled:opacity-60 transition-all backdrop-blur-sm"
                              title="Создать артикул"
                            >
                              {isCreating ? (
                                <Icon name="loader-2" className="w-4 h-4 shrink-0 animate-spin" />
                              ) : (
                                <Icon name="cloud" className="w-4 h-4 shrink-0" />
                              )}
                              <span className="hidden gallery-btn-label">Артикул</span>
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (
                                confirm(file.type === 'linked' ? 'Отвязать ссылку?' : 'Удалить файл?')
                              ) {
                                dataService.deleteFile({ filename: file.name }).then(init);
                              }
                            }}
                            className="flex items-center gap-1.5 px-2.5 py-2 bg-red-500/15 hover:bg-red-500/30 border border-red-400/20 rounded-lg text-red-300 hover:text-red-200 text-xs font-medium transition-all backdrop-blur-sm"
                            title={file.type === 'linked' ? 'Отвязать' : 'Удалить'}
                          >
                            <Icon name="trash-2" className="w-4 h-4 shrink-0" />
                            <span className="hidden gallery-btn-label">{file.type === 'linked' ? 'Отвязать' : 'Удалить'}</span>
                          </button>
                        </div>
                      </div>

                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-indigo-500 rounded-full p-1 shadow-lg z-20 ring-2 ring-white/20">
                          <Icon name="check" className="w-3 h-3 text-white" />
                        </div>
                      )}
                      {file.type === 'linked' && !isSelected && (
                        <div
                          className="absolute top-2 right-2 bg-emerald-500/80 rounded-full p-1.5 shadow-lg z-20 backdrop-blur-md border border-white/20"
                          title="Внешняя ссылка"
                        >
                          <Icon name="link" className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3 pt-6 z-20">
                        <p className="text-[10px] text-slate-200 truncate text-center font-medium drop-shadow-md">
                          {file.name
                            .split('/')
                            .pop()
                            ?.replace(/\.[^/.]+$/, '')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {totalPages > 1 && !areFoldersLoading && (
          <div className="flex justify-center items-center py-6 gap-4 border-t border-slate-800/50 mt-4">
            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 transition-all border border-slate-700"
            >
              <Icon name="chevron-left" className="w-5 h-5" />
            </button>
            <span className="text-sm font-bold text-slate-400 bg-slate-800 px-4 py-2 rounded-lg border border-slate-700">
              {currentPage} / {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 transition-all border border-slate-700"
            >
              <Icon name="chevron-right" className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedFiles.size > 0 && !isWiteAiMode && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900/90 backdrop-blur-xl border border-slate-700 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-6 z-50">
          <span className="text-white font-bold text-sm bg-indigo-600 px-2 py-0.5 rounded-md">
            {selectedFiles.size}
          </span>
          <div className="h-4 w-px bg-slate-700" />
          <button
            onClick={handleBulkDelete}
            className="text-red-400 hover:text-red-300 flex items-center gap-2 text-sm font-bold"
          >
            <Icon name="trash-2" className="w-4 h-4" />
            Удалить
          </button>
          <button
            onClick={() => setSelectedFiles(new Set())}
            className="text-slate-500 hover:text-white text-sm font-medium"
          >
            Отмена
          </button>
        </div>
      )}

      {/* Preview Modal */}
      {previewFile && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 fade-in"
          onClick={() => {
            setPreviewFile(null);
            setPreviewZoom(1);
          }}
        >
          <div
            className="bg-slate-900 rounded-xl max-w-5xl w-full max-h-[90vh] p-4 border border-slate-700 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-white truncate">
                {previewFile.name || 'Предпросмотр'}
              </h3>
              <button
                onClick={() => {
                  setPreviewFile(null);
                  setPreviewZoom(1);
                }}
                className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-white"
              >
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center justify-center max-h-[75vh] overflow-auto">
              <img
                src={previewFile.url}
                alt={previewFile.name}
                style={{ transform: `scale(${previewZoom})`, transition: 'transform 0.2s' }}
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={() => setPreviewZoom(Math.max(0.5, previewZoom - 0.2))}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded text-slate-200 text-sm"
              >
                -
              </button>
              <span className="text-xs text-slate-400 w-14 text-center">
                {Math.round(previewZoom * 100)}%
              </span>
              <button
                onClick={() => setPreviewZoom(Math.min(3, previewZoom + 0.2))}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded text-slate-200 text-sm"
              >
                +
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-6 right-6 z-[60] px-4 py-3 rounded-xl border shadow-2xl backdrop-blur text-sm font-medium ${toast.kind === 'error' ? 'bg-red-500/10 border-red-500/40 text-red-200' : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'}`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};
